import { useState } from "react"
import type { FormEvent } from "react"
import { useNavigate } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import ModuleLayout from "@/components/admin/ModuleLayout"
import { inputCls, labelCls, btnSave, btnSecondary } from "@/components/admin/FormComponents"
import { TempPasswordReveal } from "@/components/admin/TempPasswordReveal"
import { staffUsersAdmin, type StaffUserRoleAssignment } from "@/lib/api/staffUsersClient"
import { staffRolesAdmin } from "@/lib/api/staffRolesClient"
import { toastError } from "@/lib/toast"
import { STAFF_PROFILE_LABELS, STAFF_PROFILES, type StaffProfile } from "@alascorporation/access-control"

type RoleSelection = `profile:${StaffProfile}` | `role:${string}`

function roleSelectionToAssignment(selection: RoleSelection): StaffUserRoleAssignment {
  const [kind, value] = selection.split(":") as ["profile" | "role", string]
  return kind === "profile" ? { profile: value as StaffProfile } : { roleId: value }
}

export default function StaffUserFormPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: "", email: "", role: "profile:instructor" as RoleSelection })
  const [reveal, setReveal] = useState<{ label: string; value: string } | null>(null)

  const { data: customRoles = [] } = useQuery({
    queryKey: ["staff-roles"],
    queryFn: () => staffRolesAdmin.list(),
    select: (roles) => roles.filter((r) => !r.isBuiltIn),
  })

  const createMutation = useMutation({
    mutationFn: () => staffUsersAdmin.create({ name: form.name, email: form.email, ...roleSelectionToAssignment(form.role) }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["server-table", "/admin/staff-users"] })
      setReveal({ label: `Contraseña temporal para ${result.user.name}`, value: result.tempPassword })
    },
    onError: (err) => toastError(err),
  })

  function goBack() {
    navigate("/settings/users")
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    createMutation.mutate()
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Sistema", { label: "Usuarios", to: "/settings/users" }, "Nueva"]}
      title="Nueva cuenta"
      subtitle="Cuentas de administradores, docentes y gestores CCCD"
      tabs={[]}
      metrics={[]}
    >
      {reveal ? (
        <div className="mb-5">
          <TempPasswordReveal label={reveal.label} value={reveal.value} onDismiss={goBack} />
        </div>
      ) : (
        <form onSubmit={onSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Nombre</label>
              <input
                required
                autoFocus
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelCls}>Correo</label>
              <input
                required
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelCls}>Rol</label>
              <select
                className={inputCls}
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as RoleSelection }))}
              >
                <optgroup label="Perfiles predefinidos">
                  {STAFF_PROFILES.map((profile) => (
                    <option key={profile} value={`profile:${profile}`}>
                      {STAFF_PROFILE_LABELS[profile]}
                    </option>
                  ))}
                </optgroup>
                {customRoles.length > 0 ? (
                  <optgroup label="Roles personalizados">
                    {customRoles.map((role) => (
                      <option key={role.id} value={`role:${role.id}`}>
                        {role.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" className={`min-h-11 ${btnSecondary}`} onClick={goBack}>
              Cancelar
            </button>
            <button type="submit" className={`min-h-11 ${btnSave}`} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creando..." : "Crear cuenta"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
